import { ADMISSION_STATUSES } from '../../constants/admissionStatus';
import { EMPTY_FILTERS, hasActiveFilters } from './caseFilterUtils';

/** True when the chip filters are active, or an assignee narrows the toolbar's own select. */
function filtersAreActive(filters, assignee, members) {
    return hasActiveFilters(filters) || (Boolean(members) && assignee != null && assignee !== 'all');
}

export const RISK_OPTIONS = [
    ['low', 'Low risk'],
    ['medium', 'Medium risk'],
    ['high', 'High risk'],
];

function toggle(list, value) {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function ChipGroup({ label, options, selected, onToggle }) {
    if (options.length === 0) return null;
    return (
        <div className="case-filter-group" role="group" aria-label={label}>
            <span className="case-filter-group-label">{label}</span>
            {options.map(([value, text]) => {
                const active = selected.includes(value);
                return (
                    <button
                        key={value}
                        type="button"
                        className={`case-filter-chip${active ? ' is-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => onToggle(value)}
                    >
                        {text}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Search box, chip groups, and the optional assignee select for the case list.
 *
 * Controlled: `filters` is the current value and `onChange` receives a whole
 * new filters object. The assignee select renders only when `members` is given.
 */
export default function CaseFilters({
    filters,
    onChange,
    insuranceOptions,
    assignee,
    onAssigneeChange,
    members,
    onClearAll,
}) {
    const set = (patch) => onChange({ ...filters, ...patch });
    const statusOptions = ADMISSION_STATUSES.map((s) => [s, s]);
    const insurance = insuranceOptions.map((s) => [s, s]);

    return (
        <div className="case-filters">
            <div className="case-filters-row">
                <div className="case-search">
                    <input
                        type="search"
                        className="case-search-input"
                        aria-label="Search cases"
                        placeholder="Search by patient name or MRN"
                        value={filters.query}
                        onChange={(e) => set({ query: e.target.value })}
                    />
                    {filters.query && (
                        <button
                            type="button"
                            className="case-search-clear"
                            aria-label="Clear search"
                            onClick={() => set({ query: '' })}
                        >
                            ×
                        </button>
                    )}
                </div>
                {members && (
                    <label className="sort-control">
                        <span className="sort-label">Assigned to:</span>
                        <select
                            className="sort-select"
                            aria-label="Assigned to"
                            value={assignee}
                            onChange={(e) => onAssigneeChange(e.target.value)}
                        >
                            <option value="all">Everyone</option>
                            {Object.entries(members).map(([uid, m]) => (
                                <option key={uid} value={uid}>{m.name || uid}</option>
                            ))}
                        </select>
                    </label>
                )}
                {filtersAreActive(filters, assignee, members) && (
                    <button
                        type="button"
                        className="case-filters-clear"
                        onClick={() => (onClearAll ? onClearAll() : onChange(EMPTY_FILTERS))}
                    >
                        Clear filters
                    </button>
                )}
            </div>
            <ChipGroup label="Status" options={statusOptions} selected={filters.statuses} onToggle={(v) => set({ statuses: toggle(filters.statuses, v) })} />
            <ChipGroup label="Insurance" options={insurance} selected={filters.insurances} onToggle={(v) => set({ insurances: toggle(filters.insurances, v) })} />
            <ChipGroup label="Risk" options={RISK_OPTIONS} selected={filters.risks} onToggle={(v) => set({ risks: toggle(filters.risks, v) })} />
        </div>
    );
}
